import React, { useState } from 'react';
import {
  Stethoscope,
  Heart,
  Shield,
  ShieldCheck,
  Activity,
  ArrowRight,
  CheckCircle2,
  Terminal,
  Cpu,
  Zap,
  Layers,
  Smartphone,
  Pill,
  ClipboardList,
  Database,
  Search,
  Building2,
  GraduationCap,
  Building,
  FlaskConical,
  HeartHandshake,
  Rocket,
  FileText,
  Lock,
  Server,
  Cloud,
  Code,
  Workflow,
  Brain,
  Eye,
  FileCheck,
  LineChart,
  Users,
  Calendar,
  DollarSign,
  Star,
  MessageSquare,
  Send,
  BookOpen,
  Globe,
  Award,
  Check,
  CheckCircle,
  BarChart3,
  Flame,
  Radio,
  FileSpreadsheet,
  Microscope,
  Cross,
  Clock,
  Sparkles,
  PhoneCall,
  Video,
  Monitor,
  ExternalLink,
  ChevronRight,
  TrendingUp,
  RefreshCw,
  Sliders
} from 'lucide-react';
import { Button } from '../components/ui/button';

const Healthcare = () => {
  const [activeEmrTab, setActiveEmrTab] = useState('prescription');

  const emrShowcaseData = {
    prescription: {
      title: 'Physician-Centric E-Prescriptions & Vitals Charting',
      desc: 'Empowers doctors to record encounter notes, search ICD-10 diagnostic codes, generate automated dosage e-prescriptions, and attach electronic signatures in under 30 seconds.',
      image: '/images/hms_emr_prescription_suite.png',
      badge: 'ICD-10 & Voice Dictation',
      color: 'from-cyan-500 to-blue-600',
      highlights: [
        'Automated Drug-Drug Interaction Warning Alerts',
        'ICD-10 Diagnostic Standardization Auto-Suggest',
        'Encrypted One-Tap Digital Signature Approval',
        'Direct Pharmacy POS Auto-Dispatch'
      ]
    },
    interoperability: {
      title: 'HL7 FHIR v4 Laboratory & EHR Sync',
      desc: 'Seamlessly links hospital pathology analyzers, radiology DICOM PACS scanners, and external clinic EHRs into a unified longitudinal patient health record.',
      image: '/images/hms_clinical_emr_mockup.png',
      badge: 'HL7 / FHIR v4 Protocol',
      color: 'from-emerald-500 to-teal-600',
      highlights: [
        'Bi-Directional Pathology Machine Interfacing',
        'Barcode Sample Tracking & SMS Report Push',
        'DICOM Radiologic Image Viewer Integration',
        'Sub-180ms Longitudinal Record Query'
      ]
    },
    icu_telemetry: {
      title: 'Real-Time ICU Bed Matrix & Patient Vitals',
      desc: 'Real-time monitoring console tracking ICU bed occupancy, emergency triage queues, doctor duty rosters, and critical patient telemetry alerts across all hospital wards.',
      image: '/images/hms_hero_dashboard_mockup.png',
      badge: 'Real-Time Telemetry',
      color: 'from-violet-500 to-indigo-600',
      highlights: [
        'Live ICU & Emergency Ward Bed Matrix',
        'Sub-Second Patient Queue Kiosk Display',
        'Automated Sepsis & Vital Sign Risk Scoring',
        'Multi-Department Revenue & Clinical Telemetry'
      ]
    }
  };

  // Detailed 30 Healthcare Technology Services
  const allServices = [
    {
      id: 'hms',
      category: 'core',
      icon: Building2,
      title: 'Hospital Management Systems (HMS)',
      description: 'End-to-end hospital ERP automating OPD/IPD registration, ward bed grid, doctor scheduling, billing, and clinical administration across multi-specialty medical centers.',
      features: ['Live ICU Bed Matrix', 'Automated Token & Queue Kiosks', 'Multi-Department Billing Sync']
    },
    {
      id: 'emr',
      category: 'clinical',
      icon: FileText,
      title: 'Electronic Medical Records (EMR)',
      description: 'Physician-centric digital charting platform capturing encounter notes, ICD-10 diagnostic codes, vitals telemetry, and instant e-prescriptions with digital signatures.',
      features: ['ICD-10 Diagnostic Auto-Suggest', 'Voice-to-Text Clinical Dictation', 'Drug-Drug Interaction Auto-Alerts']
    },
    {
      id: 'ehr',
      category: 'clinical',
      icon: Database,
      title: 'Electronic Health Records (EHR)',
      description: 'Longitudinal, patient-centric health data repository supporting HL7 FHIR interoperability for multi-facility medical history sharing across national health registries.',
      features: ['HL7 FHIR Interoperability', 'Longitudinal Medical Records', 'Encrypted Patient Consent Layer']
    },
    {
      id: 'registration',
      category: 'core',
      icon: Users,
      title: 'Patient Registration Systems',
      description: 'Frictionless front-desk and kiosk self-service patient onboarding with biometric ID verification, National Health ID integration, and insurance card scanning.',
      features: ['Biometric & QR Code Check-in', 'National Health ID Integration', 'Demographic Data Standardization']
    },
    {
      id: 'appointments',
      category: 'patient',
      icon: Calendar,
      title: 'Appointment Management',
      description: 'Omnichannel scheduling engine for web, mobile, and WhatsApp booking, featuring automated SMS/Email reminders, doctor duty roster sync, and queue optimization.',
      features: ['Multi-Channel Booking Engine', 'Automated No-Show Reminders', 'Real-Time Doctor Availability Grid']
    },
    {
      id: 'lis',
      category: 'clinical',
      icon: Microscope,
      title: 'Laboratory Information Systems (LIS)',
      description: 'Bi-directional interfacing connecting clinical analyzer hardware directly to digital lab reports, automated test sample barcode tracking, and SMS report dispatch.',
      features: ['Analyzer Machine Interfacing', 'Sample Barcode Tracking', 'Auto-Verified Digital Lab Reports']
    },
    {
      id: 'ris',
      category: 'clinical',
      icon: Radio,
      title: 'Radiology Information Systems (RIS)',
      description: 'Integrated DICOM PACS viewer workflow, radiologist reporting templates, scan order tracking, and high-speed cloud image archival for CT, MRI, and X-ray studies.',
      features: ['DICOM PACS Viewer Integration', 'Structured Radiology Reports', 'High-Speed Cloud PACS Archive']
    },
    {
      id: 'pharmacy',
      category: 'operations',
      icon: Pill,
      title: 'Pharmacy Management',
      description: 'POS pharmacy software integrated with OPD/IPD prescriptions, batch & expiry date tracking, barcode dispensation, GST invoicing, and real-time inventory reordering.',
      features: ['Automatic Expiry Date Safeguards', 'Barcode Dispensation & POS', 'IPD Prescription Auto-Deduction']
    },
    {
      id: 'inventory',
      category: 'operations',
      icon: ClipboardList,
      title: 'Inventory Management',
      description: 'Centralized medical equipment, consumable supplies, and surgical stock tracking with automated reorder thresholds, supplier PO automation, and multi-warehouse sync.',
      features: ['Automated Reorder Thresholds', 'Surgical Consumables Tracking', 'Multi-Warehouse Inventory Sync']
    },
    {
      id: 'bloodbank',
      category: 'operations',
      icon: Heart,
      title: 'Blood Bank Software',
      description: 'Comprehensive donor management, ABO blood group compatibility testing, component separation (RBC, Plasma, Platelets), and cold-chain inventory telemetry tracking.',
      features: ['ABO Compatibility Matching', 'Component Separation Records', 'Cold-Chain Temperature Alerts']
    },
    {
      id: 'telemedicine',
      category: 'patient',
      icon: Video,
      title: 'Telemedicine Platform',
      description: 'HD WebRTC encrypted video consultation suite integrated with live EHR prescribing, digital payment gateways, and remote vital sign upload capabilities.',
      features: ['Encrypted WebRTC Video Consultation', 'Integrated E-Prescribing', 'Instant Cashless Payment Gateways']
    },
    {
      id: 'mhealth',
      category: 'patient',
      icon: Smartphone,
      title: 'Mobile Health Applications (mHealth)',
      description: 'Native iOS and Android patient mobile apps offering lab report downloads, medication reminders, appointment scheduling, and Bluetooth IoT wearable health sync.',
      features: ['Native iOS & Android Patient Apps', 'IoT Wearable Biometric Sync', 'Medication Refill Reminders']
    },
    {
      id: 'ai_solutions',
      category: 'ai',
      icon: Brain,
      title: 'AI-powered Healthcare Solutions',
      description: 'Machine learning models for medical imaging triage, predictive patient readmission scoring, automated clinical NLP note extraction, and conversational AI chatbots.',
      features: ['Medical Diagnostic Computer Vision', 'Predictive Readmission Scoring', 'Clinical NLP Document Extraction']
    },
    {
      id: 'cdss',
      category: 'ai',
      icon: Cpu,
      title: 'Clinical Decision Support Systems (CDSS)',
      description: 'Real-time evidence-based clinical decision guidance offering physicians automated drug interaction warnings, sepsis alert triggers, and diagnostic pathways.',
      features: ['Real-Time Drug Interaction Alerts', 'Automated Sepsis Early Warning', 'Evidence-Based Care Pathways']
    },
    {
      id: 'surveillance',
      category: 'public_health',
      icon: Flame,
      title: 'Disease Surveillance Systems',
      description: 'National and regional epidemiological tracking platforms capturing disease outbreak data, automated GIS heatmapping, and real-time communicable disease alerts.',
      features: ['Real-Time GIS Outbreak Heatmapping', 'Automated Epidemiological Reporting', 'Communicable Disease Registry']
    },
    {
      id: 'public_health',
      category: 'public_health',
      icon: Globe,
      title: 'Public Health Information Systems',
      description: 'Large-scale government healthcare analytics platforms for maternal-child health tracking, immunization registries, and community screening telemetry.',
      features: ['Maternal & Child Health Tracking', 'National Immunization Registry', 'Field Worker Telemetry Sync']
    },
    {
      id: 'cancer_registry',
      category: 'research',
      icon: Activity,
      title: 'Cancer Registry Software',
      description: 'Specialized oncology data management system capturing TNM tumor staging, chemotherapy regimens, radiotherapy outcomes, and long-term survival statistics.',
      features: ['TNM Tumor Staging Schema', 'Chemotherapy Regimen Tracking', 'Oncology Longitudinal Follow-up']
    },
    {
      id: 'research_data',
      category: 'research',
      icon: Database,
      title: 'Research Data Management',
      description: 'Secure, anonymized data warehouse infrastructure enabling clinical researchers to run cohort analysis, biostatistical queries, and genomic data studies.',
      features: ['HIPAA-De-identified Data Repository', 'Cohort Discovery Query Builder', 'Genomic Data Pipeline Sync']
    },
    {
      id: 'clinical_trials',
      category: 'research',
      icon: FlaskConical,
      title: 'Clinical Trial Management (CTMS)',
      description: 'End-to-end clinical trial protocol execution platform tracking subject recruitment, electronic data capture (EDC), adverse event logs, and regulatory audit trails.',
      features: ['Electronic Data Capture (EDC)', 'Subject Visit Schedule Automation', 'Adverse Event Audit Logging']
    },
    {
      id: 'dashboards',
      category: 'analytics',
      icon: BarChart3,
      title: 'Healthcare Dashboards',
      description: 'Executive-level operational and clinical dashboards displaying live bed occupancy, revenue cycle KPIs, doctor productivity metrics, and patient satisfaction scores.',
      features: ['Real-Time Bed & Emergency Matrix', 'Revenue Cycle Telemetry', 'Doctor Productivity Analytics']
    },
    {
      id: 'analytics',
      category: 'analytics',
      icon: LineChart,
      title: 'Analytics & Reporting',
      description: 'Advanced business intelligence suite with customizable report builders, clinical quality indicator calculation (NABH/JCI), and financial forecasting models.',
      features: ['JCI / NABH Quality Indicator Reports', 'Custom SQL & BI Drag-Drop Builder', 'Financial & Operational Forecasting']
    },
    {
      id: 'cloud_healthcare',
      category: 'infrastructure',
      icon: Cloud,
      title: 'Cloud Healthcare Solutions',
      description: 'HIPAA-compliant AWS/Azure cloud architecture featuring multi-region failover, auto-scaling EHR clusters, zero-downtime database replication, and Disaster Recovery (DR).',
      features: ['Multi-Region 99.99% Cloud Failover', 'HIPAA BAA Compliant Infrastructure', 'Automated Daily Encrypted Backups']
    },
    {
      id: 'api_integration',
      category: 'infrastructure',
      icon: Code,
      title: 'API Integration',
      description: 'Robust REST/GraphQL & HL7 FHIR API gateways connecting legacy hospital ERPs with external insurance TPA portals, national health IDs, and diagnostic devices.',
      features: ['HL7 v2 / v3 & FHIR v4 Gateways', 'Secure OAuth2 / JWT API Tokens', 'Third-Party TPA Portal Connectors']
    },
    {
      id: 'web_dev',
      category: 'patient',
      icon: Globe,
      title: 'Healthcare Website Development',
      description: 'High-speed, SEO-optimized healthcare enterprise web portals engineered with WCAG accessibility, online doctor appointment booking, and emergency call triggers.',
      features: ['Sub-1s Core Web Vitals Speed', 'WCAG 2.1 Accessibility Built-in', 'Integrated Doctor Search & Booking']
    },
    {
      id: 'patient_portals',
      category: 'patient',
      icon: Users,
      title: 'Patient Portals',
      description: 'Self-service patient web portals offering secure access to lab results, prescription histories, bill payments, and direct messaging with care teams.',
      features: ['24/7 Digital Lab Report Access', 'Instant Online Invoice Payments', 'Secure Doctor-Patient Messaging']
    },
    {
      id: 'doctor_portals',
      category: 'clinical',
      icon: Stethoscope,
      title: 'Doctor Portals',
      description: 'Mobile-responsive physician dashboards providing instant mobile access to OPD appointment lists, IPD patient rounds, critical lab alerts, and e-signatures.',
      features: ['Mobile IPD Round Checklists', 'Critical Lab Value Push Alerts', 'One-Tap E-Prescription Approval']
    },
    {
      id: 'crm',
      category: 'operations',
      icon: HeartHandshake,
      title: 'Healthcare CRM',
      description: 'Specialized healthcare customer relationship management software tracking patient acquisition campaigns, feedback surveys, follow-up calls, and loyalty programs.',
      features: ['Patient Feedback & NPS Surveys', 'Automated Preventive Care Alerts', 'Outpatient Acquisition Analytics']
    },
    {
      id: 'billing',
      category: 'operations',
      icon: DollarSign,
      title: 'Medical Billing Software',
      description: 'Automated revenue cycle management (RCM) billing engine supporting insurance pre-authorization, TPA cashless claims, itemized hospital invoices, and GST billing.',
      features: ['Cashless TPA Pre-Auth Automation', 'Itemized OPD / IPD Invoicing', 'Claim Rejection Audit Engine']
    },
    {
      id: 'erp',
      category: 'core',
      icon: Building,
      title: 'Healthcare ERP',
      description: 'Comprehensive enterprise resource planning unifying HRMS doctor payroll, supply chain, financial ledger accounting, fixed asset maintenance, and hospital operations.',
      features: ['Doctor Payroll & Roster Sync', 'Financial General Ledger', 'Fixed Asset & Biomedical Maintenance']
    },
    {
      id: 'custom_dev',
      category: 'infrastructure',
      icon: Workflow,
      title: 'Custom Healthcare Software Development',
      description: 'Bespoke healthcare software engineering tailored to specialized clinical protocols, research workflows, medical devices, and unique institutional requirements.',
      features: ['100% Tailored Clinical Workflows', 'Dedicated Healthcare Engineers', 'Full Intellectual Property Ownership']
    }
  ];

  // Client Testimonials
  const testimonials = [
    {
      name: 'Dr. Rajeshwardhar Sharma',
      role: 'Chief Medical Officer & Executive Director',
      organization: 'Apex Super Specialty Hospital Chain (1,200+ Beds)',
      image: 'https://images.unsplash.com/photo-1622253692010-333f2da6031d?auto=format&fit=crop&q=80&w=200',
      text: 'HOASP Global transformed our multi-facility hospital operations. Their enterprise HMS reduced patient OPD queue wait times by 65%, automated our pharmacy stock alerts, and seamlessly integrated our pathology analyzers with digital EHRs. Their team understands clinical workflows inside out.'
    },
    {
      name: 'Elena Rostova',
      role: 'Head of Health Information Systems',
      organization: 'European Public Health & Epidemiological Institute',
      image: 'https://images.unsplash.com/photo-1594824813571-2b533411efa0?auto=format&fit=crop&q=80&w=200',
      text: 'Engineering an offline-first mobile NCD community screening app for over 250,000 field health workers seemed impossible until we partnered with HOASP Global. Their HL7 FHIR sync pipeline and GIS surveillance dashboard are bulletproof, secure, and highly performant.'
    },
    {
      name: 'Dr. Marcus Vance',
      role: 'Dean of Health Sciences & Research Director',
      organization: 'Vance Medical College & Research Foundation',
      image: 'https://images.unsplash.com/photo-1537368910025-700350fe46c7?auto=format&fit=crop&q=80&w=200',
      text: 'The Clinical Trial Management System (CTMS) developed by HOASP Global accelerated our research trial milestone tracking by 40%. The HIPAA-de-identified data repository and automated patient consent layer give our institutional review board complete confidence.'
    }
  ];

  return (
    <div className="min-h-screen font-roboto selection:bg-cyan-500 selection:text-white pt-20 bg-[#0B0F19]" style={{ fontFamily: "'Roboto', sans-serif" }}>

      {/* SECTION 1: HERO BANNER SHOWCASE - Deep Midnight Slate & Vibrant Cyan Accent */}
      <section className="py-28 bg-gradient-to-b from-[#0B0F19] via-[#0F172A] to-[#0F172A] text-white text-left relative overflow-hidden border-b border-slate-800/80">

        {/* Glowing Orbs & Mesh Background */}
        <div className="absolute inset-0 z-0">
          <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-[120px] pointer-events-none" />
          <div className="absolute bottom-10 right-1/4 w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[120px] pointer-events-none" />
          <div className="absolute inset-0 opacity-15 bg-[radial-gradient(#38bdf8_1px,transparent_1px)] [background-size:32px_32px] pointer-events-none" />
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-12 gap-12 items-center">

            {/* Hero Text Left */}
            <div className="lg:col-span-7">
              <div className="inline-flex items-center space-x-2.5 bg-cyan-500/10 border border-cyan-500/30 rounded-full px-4 py-2 mb-6 backdrop-blur-md">
                <ShieldCheck className="w-4 h-4 text-cyan-400" />
                <span className="text-cyan-300 text-sm md:text-sm font-semibold uppercase tracking-wider ">
                  HOASP Global • Digital Healthcare Technology
                </span>
              </div>

              <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 leading-[1.15] tracking-tight">
                Empowering Global Healthcare <br />
                <span className="bg-gradient-to-r from-cyan-400 via-teal-300 to-blue-500 bg-clip-text text-transparent">
                  Through Digital Transformation
                </span>
              </h1>

              <p className="text-slate-300 text-base md:text-xl leading-relaxed mb-8 max-w-2xl font-normal">
                HOASP Global engineers secure, HIPAA-compliant, and HL7 FHIR-ready enterprise hospital management systems, EMR software, telemedicine platforms, and public health telemetry software for global healthcare leaders.
              </p>

              {/* Two Action Buttons */}
              <div className="flex flex-wrap gap-4 mb-10">
                <a href="#consultation">
                  <Button className="bg-gradient-to-r from-cyan-500 via-teal-400 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-black font-extrabold px-8 py-6 text-base rounded-xl shadow-lg shadow-cyan-500/25 transition-all duration-300 flex items-center gap-2 hover:scale-105">
                    Book Free Consultation
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </a>
                <a href="#services">
                  <Button variant="outline" className="border-slate-700 bg-slate-900/60 text-slate-200 hover:bg-slate-800 hover:text-white px-8 py-6 text-base font-semibold rounded-xl transition-all">
                    Explore 30 Services
                  </Button>
                </a>
              </div>

              {/* Trust Badges */}
              <div className="pt-8 border-t border-slate-800/80 grid grid-cols-3 gap-4  text-sm text-slate-400">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-cyan-400" />
                  <span>HIPAA & GDPR Ready</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-emerald-400" />
                  <span>HL7 / FHIR v4 API</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-violet-400" />
                  <span>99.99% SLA Uptime</span>
                </div>
              </div>

            </div>

            {/* Hero Right Interactive Mockup Window + Live Animated ECG Telemetry GIF */}
            <div className="lg:col-span-5">
              <div className="bg-[#0F172A]/90 border border-slate-800 hover:border-cyan-500/50 rounded-3xl overflow-hidden shadow-2xl relative group transition-all duration-500 backdrop-blur-xl">

                {/* Browser Bar */}
                <div className="bg-[#0B0F19] px-4 py-3 border-b border-slate-800 flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="w-3 h-3 rounded-full bg-rose-500/80 inline-block" />
                    <span className="w-3 h-3 rounded-full bg-amber-500/80 inline-block" />
                    <span className="w-3 h-3 rounded-full bg-emerald-500/80 inline-block" />
                  </div>
                  <div className="bg-[#090D16] text-cyan-300 text-sm  px-3 py-1 rounded-lg border border-slate-800 truncate max-w-xs flex items-center space-x-2">
                    <Lock className="w-3 h-3 text-emerald-400 flex-shrink-0" />
                    <span className="truncate">https://hoasp.com/digital-healthcare</span>
                  </div>
                  <span className="text-[10px]  text-emerald-400 bg-emerald-950/80 border border-emerald-800 px-2 py-0.5 rounded-full font-bold flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping" />
                    LIVE
                  </span>
                </div>

                {/* Hero Main Banner Mockup Image */}
                <div className="relative overflow-hidden bg-[#090D16]">
                  <img
                    src="/images/hms_hospitality_command_center.png"
                    alt="HOASP Global Hospitality & Clinical Operations Command Center"
                    className="w-full h-auto object-cover transform group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#090D16] via-transparent to-transparent opacity-80" />

                  {/* Hospitality Command Center Badge Overlay */}
                  <div className="absolute bottom-4 left-4 right-4 bg-[#0B0F19]/95 backdrop-blur-xl border border-slate-800 rounded-2xl p-4 flex items-center justify-between">
                    <div>
                      <div className="text-sm  font-bold text-white uppercase tracking-wider">HOSPITALITY & CLINICAL COMMAND CENTER</div>
                      <div className="text-[11px] text-slate-400 ">OPD / IPD Bed Matrix • Pharmacy POS • HL7 FHIR v4</div>
                    </div>
                    <span className="text-sm  font-bold text-emerald-400 bg-emerald-950/80 border border-emerald-800 px-3 py-1 rounded-xl">
                      ENTERPRISE HMS
                    </span>
                  </div>
                </div>

              </div>
            </div>

          </div>
        </div>
      </section>

      {/* SECTION 2: ABOUT DIGITAL HEALTHCARE SOLUTIONS - Soft Pearl Alabaster Background */}
      <section className="py-28 bg-[#F8FAFC] text-slate-900 text-left border-b border-slate-200">
        <div className="container mx-auto px-6">

          <div className="grid lg:grid-cols-12 gap-12 items-center mb-16">

            <div className="lg:col-span-6">
              <div className="inline-block bg-cyan-100 border border-cyan-200 rounded-full px-4 py-1.5 mb-4">
                <span className="text-cyan-900 text-sm font-bold uppercase tracking-widest ">
                  Transforming Healthcare Delivery
                </span>
              </div>
              <h2 className="text-3xl md:text-5xl font-extrabold text-[#0F172A] mb-6 leading-tight">
                Modernizing Healthcare Through <span className="text-slate-500">Intelligent Technology</span>
              </h2>
              <p className="text-slate-600 text-base md:text-lg leading-relaxed mb-6">
                Digital transformation in healthcare is no longer just about digitizing paper forms; it is about building interconnected, intelligent, and secure health software ecosystems. At HOASP Global, we empower medical institutions to enhance patient outcomes, optimize clinical workflows, enforce zero-trust security, and accelerate medical research.
              </p>
              <div className="flex flex-wrap gap-6 text-sm font-semibold text-slate-800 ">
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-cyan-600 mr-2" />
                  <span>100% HIPAA Ready</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-emerald-600 mr-2" />
                  <span>HL7 & FHIR Standards</span>
                </div>
              </div>
            </div>

            {/* Real Healthcare Facility Original Image + Motion Card */}
            <div className="lg:col-span-6">
              <div className="relative rounded-3xl overflow-hidden border border-slate-200 shadow-2xl group">
                <img
                  src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=800&q=80"
                  alt="Modern Hospital Digital Healthcare Center"
                  className="w-full h-80 md:h-96 object-cover transform group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 text-white">
                  <div className="text-sm  font-bold uppercase tracking-wider text-cyan-300">CLINICAL EXCELLENCE</div>
                  <div className="text-xl font-extrabold">Next-Generation Smart Hospital Systems</div>
                </div>
              </div>
            </div>

          </div>

          {/* 5 Core Pillars Grid - Interactive Motion Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {[
              { icon: Heart, title: 'Elevated Patient Care', desc: 'Empower patients with 24/7 portal access, digital lab reports, instant scheduling, and remote health tracking.', accent: 'hover:border-cyan-500' },
              { icon: Building2, title: 'Hospital ERP Efficiency', desc: 'Streamline OPD/IPD queues, ICU bed occupancy, pharmacy inventory, doctor duty rosters, and billing.', accent: 'hover:border-blue-500' },
              { icon: Activity, title: 'Disease Surveillance', desc: 'Real-time epidemiological heatmapping, automated disease outbreak notifications, and public screening.', accent: 'hover:border-emerald-500' },
              { icon: FlaskConical, title: 'Medical Research CTMS', desc: 'Accelerate clinical trial data collection (EDC), anonymized genomic databases, and research analytics.', accent: 'hover:border-violet-500' },
              { icon: ShieldCheck, title: 'HIPAA & HL7 Security', desc: 'Zero-trust data architecture, AES-256 encryption, role-based access control, and compliance.', accent: 'hover:border-amber-500' }
            ].map((pillar, idx) => (
              <div key={idx} className={`bg-white border border-slate-200 rounded-2xl p-6 transition-all duration-300 shadow-sm hover:shadow-xl hover:-translate-y-1.5 ${pillar.accent}`}>
                <div className="w-12 h-12 bg-[#0F172A] rounded-xl flex items-center justify-center mb-4 text-white shadow-md">
                  <pillar.icon className="w-6 h-6 text-cyan-400" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{pillar.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{pillar.desc}</p>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* SECTION 3: LEAD CONVERSION BREAK 1 - Rich Navy Gradient */}
      <section className="py-12 bg-gradient-to-r from-[#0F172A] via-[#1E1B4B] to-[#0F172A] text-white border-b border-slate-800">
        <div className="container mx-auto px-6 text-center">
          <div className="bg-[#fff] border border-slate-800 rounded-3xl p-8 flex flex-col md:flex-row items-center justify-between gap-6 shadow-2xl backdrop-blur-xl">
            <div className="text-left">
              <h3 className="text-2xl font-extrabold text-black mb-2">Planning to Modernize Your Healthcare Infrastructure?</h3>
              <p className="text-black text-sm">Speak with HOASP Global healthcare software architects for a tailored digital transformation roadmap.</p>
            </div>
            <a href="#consultation">
              <Button className="bg-gradient-to-r from-cyan-400 to-blue-600 hover:from-cyan-500 hover:to-blue-700 text-black font-extrabold px-8 py-4 rounded-xl flex-shrink-0 shadow-lg shadow-cyan-500/20 hover:scale-105 transition-all">
                Book Architecture Call →
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* SECTION 4: OUR HEALTHCARE TECHNOLOGY SERVICES - Dark Charcoal Slate */}
      <section id="services" className="py-28 bg-[#0F172A] text-white text-left border-b border-slate-800/80">
        <div className="container mx-auto px-6">

          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-block bg-cyan-500/10 border border-cyan-500/20 rounded-full px-4 py-1.5 mb-4">
              <span className="text-cyan-400 text-sm font-bold uppercase tracking-widest ">
                Full-Spectrum Healthcare Services
              </span>
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4">
              Comprehensive Healthcare <span className="text-slate-400">Technology Suite</span>
            </h2>
            <p className="text-slate-400 text-base md:text-lg">
              Explore our 30 specialized digital healthcare solutions engineered for hospitals, public health departments, research institutes, and healthcare startups.
            </p>


          </div>

          {/* Latest Trend Interactive Clinical EMR Showcase Console */}
          <div className="mb-16 bg-gradient-to-br from-[#0B0F19] via-[#0F172A] to-[#1E1B4B] border border-slate-800 rounded-3xl p-8 md:p-12 shadow-2xl text-white relative overflow-hidden">

            {/* Top Bar */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8 pb-6 border-b border-slate-800">
              <div>
                <div className="inline-flex items-center space-x-2 bg-cyan-500/10 border border-cyan-500/30 px-3 py-1 rounded-full text-sm  font-bold text-cyan-400 mb-2">
                  <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
                  <span>NEXT-GEN CLINICAL EMR ENGINE</span>
                </div>
                <h3 className="text-2xl md:text-3xl font-extrabold text-white">
                  Clinical EMR & Digital Prescription Suite
                </h3>
              </div>

              {/* Tab Switcher Buttons */}
              <div className="inline-flex flex-wrap p-1.5 bg-[#0B0F19] border border-slate-800 rounded-2xl gap-2  text-sm">
                <button
                  onClick={() => setActiveEmrTab('prescription')}
                  className={`px-4 py-2 rounded-xl transition-all font-bold ${activeEmrTab === 'prescription'
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-black shadow-md'
                    : 'text-slate-400 hover:text-white'
                    }`}
                >
                  E-Prescription & Vitals
                </button>
                <button
                  onClick={() => setActiveEmrTab('interoperability')}
                  className={`px-4 py-2 rounded-xl transition-all font-bold ${activeEmrTab === 'interoperability'
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-black shadow-md'
                    : 'text-slate-400 hover:text-white'
                    }`}
                >
                  HL7 FHIR & Lab Sync
                </button>
                <button
                  onClick={() => setActiveEmrTab('icu_telemetry')}
                  className={`px-4 py-2 rounded-xl transition-all font-bold ${activeEmrTab === 'icu_telemetry'
                    ? 'bg-gradient-to-r from-violet-500 to-indigo-600 text-white shadow-md'
                    : 'text-slate-400 hover:text-white'
                    }`}
                >
                  ICU Bed Matrix
                </button>
              </div>
            </div>

            {/* Showcase Grid Content */}
            <div className="grid lg:grid-cols-12 gap-8 items-center">

              {/* Left Details */}
              <div className="lg:col-span-6">
                <h4 className="text-2xl font-bold text-white mb-4">
                  {emrShowcaseData[activeEmrTab].title}
                </h4>
                <p className="text-slate-300 text-sm leading-relaxed mb-6">
                  {emrShowcaseData[activeEmrTab].desc}
                </p>

                {/* Checklist Grid */}
                <div className="space-y-3 mb-8">
                  {emrShowcaseData[activeEmrTab].highlights.map((item, idx) => (
                    <div key={idx} className="flex items-center text-sm md:text-sm text-slate-200 bg-[#0B0F19]/90 border border-slate-800 p-3 rounded-xl">
                      <CheckCircle2 className="w-4 h-4 text-cyan-400 mr-3 flex-shrink-0" />
                      <span className="font-medium">{item}</span>
                    </div>
                  ))}
                </div>

                <div className="flex items-center space-x-4  text-sm">
                  <span className="bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 px-3.5 py-1.5 rounded-full font-bold">
                    {emrShowcaseData[activeEmrTab].badge}
                  </span>
                  <span className="text-slate-400">HIPAA & HL7 Compliant</span>
                </div>
              </div>

              {/* Right Interactive Image Showcase Box + Scanning Line Motion */}
              <div className="lg:col-span-6">
                <div className="rounded-2xl overflow-hidden border border-slate-800 bg-black shadow-2xl relative group">
                  <img
                    src={emrShowcaseData[activeEmrTab].image}
                    alt={emrShowcaseData[activeEmrTab].title}
                    className="w-full h-auto object-cover transform group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19] via-transparent to-transparent opacity-60 pointer-events-none" />

                  {/* Medical Scanner Line Animation GIF */}
                  <div className="absolute top-3 left-3 bg-[#0B0F19]/90 border border-emerald-500/40 backdrop-blur-md px-3 py-1.5 rounded-xl  text-[11px] text-emerald-300 flex items-center gap-2 shadow-lg">
                    <img src="https://media.giphy.com/media/v1.Y2lkPTc5MGI3NjExZjdhY2RkM2Nma3o2Ynk4b2ZldW9jODRrcmRwbHBtcDN1cHRiZXNndyZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/26tn33aiTi1jkl6H6/giphy.gif" alt="Digital Health Radar Sync" className="w-5 h-5 rounded-full object-cover border border-emerald-400" />
                    <span>LIMS ANALYZER SYNC: ACTIVE</span>
                  </div>

                </div>
              </div>

            </div>

          </div>

          {/* Services Grid (Dark Glass Cards with Hover Scale) */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {allServices.map(service => (
              <div
                key={service.id}
                className="group bg-[#1E293B]/80 border border-slate-700/80 hover:border-cyan-400 rounded-3xl p-8 transition-all duration-300 shadow-md hover:shadow-2xl hover:-translate-y-1.5 flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <div className="w-12 h-12 bg-[#0F172A] border border-slate-700 rounded-2xl flex items-center justify-center group-hover:bg-gradient-to-br group-hover:from-cyan-500 group-hover:to-blue-600 group-hover:text-black transition-all duration-300">
                      <service.icon className="w-6 h-6 text-cyan-400 group-hover:text-black transition-colors" />
                    </div>
                    <span className="text-[10px]  font-bold uppercase tracking-wider text-slate-300 bg-[#0F172A] border border-slate-700 px-3 py-1 rounded-full">
                      {service.category.toUpperCase()}
                    </span>
                  </div>

                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-cyan-300 transition-colors">
                    {service.title}
                  </h3>

                  <p className="text-slate-300 text-sm leading-relaxed mb-6">
                    {service.description}
                  </p>
                </div>

                <div className="pt-4 border-t border-slate-700/80 space-y-2  text-sm text-slate-300">
                  {service.features.map((feat, idx) => (
                    <div key={idx} className="flex items-center">
                      <CheckCircle className="w-3.5 h-3.5 text-cyan-400 mr-2 flex-shrink-0" />
                      <span>{feat}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* SECTION 5: WHY CHOOSE HOASP GLOBAL - Warm Alabaster Gray */}
      <section className="py-28 bg-[#F1F5F9] text-slate-900 text-left border-b border-slate-200">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-block bg-slate-200 border border-slate-300 rounded-full px-4 py-1.5 mb-4">
              <span className="text-slate-800 text-sm font-bold uppercase tracking-widest ">
                Institutional Differentiators
              </span>
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 mb-4">
              Why Healthcare Leaders Choose <span className="text-slate-500">HOASP Global</span>
            </h2>
            <p className="text-slate-600 text-base md:text-lg">
              We combine deep healthcare domain expertise with modern software architecture to deliver reliable, secure, and future-proof digital solutions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { title: 'Custom Development', desc: '100% bespoke software tailored precisely to your hospital clinical protocols and administrative workflows.', icon: Code, accent: 'hover:border-cyan-500' },
              { title: 'Secure Architecture', desc: 'Zero-trust network security, immutable audit logs, and automated vulnerability pen-testing built into every release.', icon: ShieldCheck, accent: 'hover:border-blue-500' },
              { title: 'HIPAA-Ready Architecture', desc: 'Compliant with HIPAA physical, administrative, and technical safeguards for Protected Health Information (PHI).', icon: Lock, accent: 'hover:border-emerald-500' },
              { title: 'HL7 / FHIR Integration Support', desc: 'Native HL7 v2/v3 and FHIR v4 API gateways connecting lab equipment, PACS imaging, and EHR repositories.', icon: Server, accent: 'hover:border-violet-500' },
              { title: 'Scalable Microservices', desc: 'Cloud-native microservice clusters designed to scale seamlessly from 50-bed clinics to 5,000+ bed hospital networks.', icon: Layers, accent: 'hover:border-indigo-500' },
              { title: 'AI & ML Integration', desc: 'Machine learning for predictive patient readmission, voice-to-text clinical notes, and automated medical image triage.', icon: Brain, accent: 'hover:border-rose-500' },
              { title: 'Modern UI/UX Design', desc: 'Intuitive, low-click interface design optimized for busy doctors, nurses, administrative staff, and patients.', icon: Smartphone, accent: 'hover:border-amber-500' },
              { title: 'Flexible Cloud Deployment', desc: 'Deploy on AWS, Azure, Google Cloud BAA clusters, or air-gapped on-premise hospital local server hardware.', icon: Cloud, accent: 'hover:border-[#0F172A]' },
              { title: 'High Performance & Speed', desc: 'Sub-180ms database query response times ensuring instant clinical EHR record retrieval even during peak OPD hours.', icon: Zap, accent: 'hover:border-teal-500' },
              { title: 'Role-Based Access Control (RBAC)', desc: 'Granular user permission matrices governing doctor, nurse, pharmacist, lab technician, and billing clerk views.', icon: Users, accent: 'hover:border-cyan-500' },
              { title: 'End-to-End Data Encryption', desc: 'AES-256 encryption for database records at rest and TLS 1.3 encryption for all data transmissions.', icon: Lock, accent: 'hover:border-blue-500' },
              { title: 'Long-Term SLA Technical Support', desc: '24/7 dedicated L1/L2/L3 technical support, continuous security updates, and guaranteed SLA response times.', icon: PhoneCall, accent: 'hover:border-emerald-500' }
            ].map((item, idx) => (
              <div key={idx} className={`bg-white border border-slate-200 rounded-3xl p-8 transition-all duration-300 shadow-sm hover:shadow-xl hover:-translate-y-1.5 ${item.accent}`}>
                <div className="w-12 h-12 bg-[#0F172A] rounded-2xl flex items-center justify-center mb-6 text-cyan-400 shadow-md">
                  <item.icon className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 6: INDUSTRIES WE SERVE - Deep Midnight Slate */}
      <section className="py-28 bg-[#0B0F19] text-white text-left border-b border-slate-800">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-block bg-emerald-500/10 border border-emerald-500/20 rounded-full px-4 py-1.5 mb-4">
              <span className="text-emerald-400 text-sm font-bold uppercase tracking-widest ">
                Target Sectors & Institutions
              </span>
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4">
              Healthcare Ecosystems <span className="text-slate-400">We Empower</span>
            </h2>
            <p className="text-slate-400 text-base md:text-lg">
              Tailored digital health infrastructure for private, public, academic, and non-profit institutions worldwide.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
            {[
              { name: 'Hospitals & Health Chains', icon: Building2, desc: 'Multi-specialty, OPD/IPD, ICU bed matrix' },
              { name: 'Government Health Depts', icon: Globe, desc: 'National NCD, disease surveillance, public health' },
              { name: 'Medical Colleges', icon: GraduationCap, desc: 'Teaching hospital ERP, student & clinical records' },
              { name: 'Research Institutes', icon: FlaskConical, desc: 'CTMS, bio-banking, genomic research repos' },
              { name: 'Health NGOs & Foundations', icon: HeartHandshake, desc: 'Field community screening, mobile care apps' },
              { name: 'Diagnostic Laboratories', icon: Microscope, desc: 'LIMS, automated machine analyzer sync' },
              { name: 'Healthcare Startups', icon: Rocket, desc: 'Telemedicine, mHealth, AI diagnostic MVP to scale' },
              { name: 'Health Insurance & TPAs', icon: ShieldCheck, desc: 'Cashless claims engine, pre-auth portals' }
            ].map((ind, idx) => (
              <div key={idx} className="bg-[#1E293B] border border-slate-800 rounded-2xl p-6 hover:border-emerald-400 transition-all duration-300 hover:-translate-y-1 text-center shadow-md">
                <div className="w-12 h-12 bg-[#090D16] border border-slate-700 rounded-2xl flex items-center justify-center mx-auto mb-4 text-emerald-400">
                  <ind.icon className="w-6 h-6" />
                </div>
                <h3 className="text-base font-bold text-white mb-1">{ind.name}</h3>
                <p className="text-slate-400 text-sm leading-normal">{ind.desc}</p>
              </div>
            ))}
          </div>

          {/* Real Medical Laboratory Original Image Showcase */}
          <div className="rounded-3xl overflow-hidden border border-slate-800 shadow-2xl relative group">
            <img
              src="https://images.unsplash.com/photo-1584515979956-d9f6e5d09982?auto=format&fit=crop&w=1200&q=80"
              alt="Diagnostic Laboratory Technology Integration"
              className="w-full h-64 md:h-80 object-cover transform group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0B0F19]/90 via-[#0B0F19]/60 to-transparent flex items-center p-8 md:p-12 text-white">
              <div className="max-w-xl">
                <span className="text-sm  font-bold uppercase tracking-wider text-emerald-400">DIAGNOSTIC & RESEARCH ENGINE</span>
                <h3 className="text-2xl md:text-4xl font-extrabold mb-3">Connecting Laboratories & Clinical Data</h3>
                <p className="text-slate-300 text-sm leading-relaxed">
                  Our LIMS and PACS software connects pathology analyzers and radiologic imaging directly with central hospital EHRs.
                </p>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* SECTION 7: SECURITY & COMPLIANCE - Crisp White Background */}
      <section className="py-28 bg-white text-slate-900 text-left border-b border-slate-200">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-12 items-center">

            <div className="lg:col-span-6">
              <div className="inline-flex items-center space-x-2 bg-blue-50 border border-blue-200 rounded-full px-4 py-1.5 mb-6  text-sm">
                <Lock className="w-4 h-4 text-blue-600" />
                <span className="text-blue-900 font-bold uppercase">Zero-Trust Clinical Security</span>
              </div>
              <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 mb-6">
                Enterprise Security & <span className="text-slate-500">Global Compliance</span>
              </h2>
              <p className="text-slate-600 text-base leading-relaxed mb-8">
                Protecting patient records and health data privacy is paramount. HOASP Global adheres to strict international regulatory benchmarks, enforcing encrypted data stores, role-based access control, and continuous audit trails.
              </p>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-slate-50 border border-slate-200 p-4 rounded-2xl shadow-xs">
                  <div className="font-bold text-sm mb-1">HIPAA-Ready</div>
                  <div className="text-slate-600">Compliant PHI data safeguards</div>
                </div>
                <div className="bg-slate-50 border border-slate-200 p-4 rounded-2xl shadow-xs">
                  <div className="text-slate-900 font-bold text-sm mb-1">HL7 & FHIR v4</div>
                  <div className="text-slate-600">Universal clinical data protocol</div>
                </div>
                <div className="bg-slate-50 border border-slate-200 p-4 rounded-2xl shadow-xs">
                  <div className="text-slate-900 font-bold text-sm mb-1">Role-Based Access</div>
                  <div className="text-slate-600">Granular RBAC user privileges</div>
                </div>
                <div className="bg-slate-50 border border-slate-200 p-4 rounded-2xl shadow-xs">
                  <div className="text-slate-900 font-bold text-sm mb-1">AES-256 Encryption</div>
                  <div className="text-slate-600">Data encrypted at rest & transit</div>
                </div>
                <div className="bg-slate-50 border border-slate-200 p-4 rounded-2xl shadow-xs">
                  <div className="text-slate-900 font-bold text-sm mb-1">Automated Backups</div>
                  <div className="text-slate-600">Encrypted multi-region snapshots</div>
                </div>
                <div className="bg-slate-50 border border-slate-200 p-4 rounded-2xl shadow-xs">
                  <div className="text-slate-900 font-bold text-sm mb-1">GDPR-Ready Architecture</div>
                  <div className="text-slate-600">Right-to-be-forgotten & consent</div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-6 bg-slate-50 border border-slate-200 rounded-3xl p-8 shadow-xl">
              <div className="flex items-center space-x-3 mb-6 pb-4 border-b border-slate-200">
                <ShieldCheck className="w-8 h-8 text-blue-600" />
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Compliance & Audit Readiness</h3>
                  <p className="text-sm text-slate-500 ">Continuous Vulnerability & Penetration Testing</p>
                </div>
              </div>

              <div className="space-y-4 text-sm text-slate-700">
                <div className="bg-white p-4 rounded-xl border border-slate-200 flex items-start space-x-3 shadow-xs">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-bold text-slate-900 block">Immutable Audit Logging</span>
                    <span className="text-sm text-slate-600">Tracks every patient record view, edit, prescription change, and lab entry for legal compliance.</span>
                  </div>
                </div>

                <div className="bg-white p-4 rounded-xl border border-slate-200 flex items-start space-x-3 shadow-xs">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-bold text-slate-900 block">Patient Consent Management</span>
                    <span className="text-sm text-slate-600">Granular consent popups allowing patients to grant or revoke medical record access to external clinics.</span>
                  </div>
                </div>

                <div className="bg-white p-4 rounded-xl border border-slate-200 flex items-start space-x-3 shadow-xs">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-bold text-slate-900 block">High Availability Disaster Recovery (DR)</span>
                    <span className="text-sm text-slate-600">Multi-cloud database replication guarantees 99.99% system availability even during local server outages.</span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* SECTION 8: BUSINESS BENEFITS & COMPARISON TABLE - Dark Executive Slate */}
      <section className="py-28 bg-[#0F172A] text-white text-left border-b border-slate-800">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-block bg-cyan-500/10 border border-cyan-500/20 rounded-full px-4 py-1.5 mb-4">
              <span className="text-cyan-400 text-sm font-bold uppercase tracking-widest ">
                Measurable Impact
              </span>
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4">
              Business & Clinical <span className="text-slate-400">Benefits</span>
            </h2>
            <p className="text-slate-400 text-base md:text-lg">
              Replacing legacy paper records with HOASP Global digital solutions yields immediate operational and financial returns.
            </p>
          </div>

          {/* Comparison Table */}
          <div className="bg-[#1E293B] border border-slate-700/80 rounded-3xl overflow-hidden mb-16 font-sans shadow-2xl">
            <div className="grid grid-cols-12 bg-[#0B0F19] p-4  text-sm font-bold text-slate-400 border-b border-slate-800 uppercase tracking-wider">
              <div className="col-span-4">Operational Area</div>
              <div className="col-span-4 text-rose-400">Legacy / Paper Workflows</div>
              <div className="col-span-4 text-cyan-300">HOASP Global Digital Healthcare</div>
            </div>

            {[
              { area: 'OPD Queue & Registration', legacy: '45-60 min wait times, manual paper files', digital: 'Sub-5 min kiosk check-in, QR token' },
              { area: 'Diagnostic Lab Reports', legacy: 'Manual paper entry, 24-48 hr delays', digital: 'Bi-directional LIMS, instant digital SMS' },
              { area: 'Medical Record Access', legacy: 'Lost physical files, data silos', digital: 'Sub-180ms instant EHR retrieval' },
              { area: 'Pharmacy Stock Tracking', legacy: 'Frequent expiry loss & stock-outs', digital: 'Automated barcode alerts & reorder' },
              { area: 'TPA Insurance Claims', legacy: 'High rejection rates, 30+ day delays', digital: 'Automated pre-auth, instant portal sync' },
              { area: 'Regulatory Compliance', legacy: 'Manual audit risk, missing records', digital: 'Immutable digital logs, HIPAA ready' }
            ].map((row, idx) => (
              <div key={idx} className="grid grid-cols-12 p-4 border-b border-slate-700/60 text-sm text-slate-300 hover:bg-[#0B0F19]/50 transition-colors">
                <div className="col-span-4 font-bold text-white">{row.area}</div>
                <div className="col-span-4 text-slate-400">{row.legacy}</div>
                <div className="col-span-4 font-semibold text-cyan-400 flex items-center">
                  <CheckCircle className="w-4 h-4 text-cyan-400 mr-2 flex-shrink-0" />
                  {row.digital}
                </div>
              </div>
            ))}
          </div>

          {/* 10 Business Benefits Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              'Increase Hospital Efficiency',
              'Reduce Paperwork by 95%',
              'Elevate Patient Satisfaction',
              'Faster Diagnostic Output',
              'Automated Regulatory Audits',
              'Strict Government Compliance',
              'Centralized Health Registry',
              'Real-Time Clinical Telemetry',
              'AI Workflow Automation',
              'Lower Operational Expenses'
            ].map((b, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-2xl p-4 text-center hover:border-cyan-500 transition-all duration-300 hover:-translate-y-1 shadow-sm hover:shadow-md">
                <CheckCircle className="w-6 h-6 text-cyan-600 mx-auto mb-2" />
                <span className="text-sm font-bold text-slate-900 block">{b}</span>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* SECTION 10: DEVELOPMENT PROCESS - Soft Alabaster Background */}
      <section className="py-28 bg-[#F8FAFC] text-slate-900 text-left border-b border-slate-200">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-block bg-slate-200 border border-slate-300 rounded-full px-4 py-1.5 mb-4">
              <span className="text-slate-800 text-sm font-bold uppercase tracking-widest ">
                Proven Methodology
              </span>
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 mb-4">
              Our 8-Step Healthcare <span className="text-slate-500">Development Lifecycle</span>
            </h2>
            <p className="text-slate-600 text-base md:text-lg">
              A disciplined, compliance-first software engineering process built specifically for healthcare institutions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 ">
            {[
              { step: '01', title: 'Discovery', desc: 'Clinical workflow mapping, HIPAA requirement analysis, and technical feasibility audit.' },
              { step: '02', title: 'Planning', desc: 'HL7 data schema design, microservice architecture planning, and project milestone scoping.' },
              { step: '03', title: 'UI/UX Design', desc: 'Creating intuitive, doctor-approved low-click wireframes and patient mobile app mockups.' },
              { step: '04', title: 'Development', desc: 'Agile sprint coding with encrypted microservices, LIMS hardware interfacing, and EHR modules.' },
              { step: '05', title: 'Testing', desc: 'Rigorous security penetration testing, HL7 payload validation, and clinical UAT simulations.' },
              { step: '06', title: 'Deployment', desc: 'Zero-downtime database ETL migration and cloud cluster failover deployment.' },
              { step: '07', title: 'Training', desc: 'Hands-on interactive training for doctors, nursing staff, pharmacists, and billing teams.' },
              { step: '08', title: 'SLA Support', desc: '24/7 technical monitoring, continuous system maintenance, and guaranteed SLA response.' }
            ].map((st, idx) => (
              <div key={idx} className="bg-white border border-slate-200 rounded-3xl p-6 hover:border-cyan-600 transition-all duration-300 flex flex-col justify-between shadow-sm hover:shadow-xl hover:-translate-y-1.5">
                <div>
                  <span className="text-3xl font-black text-cyan-600 block mb-2">{st.step}</span>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{st.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{st.desc}</p>
                </div>
                <div className="pt-4 border-t border-slate-200 mt-4 text-[10px] text-slate-500 uppercase tracking-wider">
                  PHASE {st.step}/08
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* SECTION 11: CLIENT TESTIMONIALS - Deep Midnight Slate */}
      <section className="py-28 bg-[#0B0F19] text-white text-left border-b border-slate-800">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-block bg-amber-500/10 border border-amber-500/20 rounded-full px-4 py-1.5 mb-4">
              <span className="text-amber-400 text-sm font-bold uppercase tracking-widest ">
                Proven Track Record
              </span>
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4">
              Trusted by Healthcare <span className="text-slate-400">Leaders</span>
            </h2>
            <p className="text-slate-400 text-base md:text-lg">
              Read how HOASP Global digital healthcare software transforms operations for hospitals, research centers, and public health agencies.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((t, idx) => (
              <div key={idx} className="bg-[#1E293B] border border-slate-800 rounded-3xl p-8 flex flex-col justify-between hover:border-amber-400 transition-all duration-300 hover:-translate-y-1.5 shadow-md">
                <div>
                  <div className="flex items-center space-x-1 text-amber-400 mb-6">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-amber-400" />
                    ))}
                  </div>
                  <p className="text-slate-300 text-sm italic leading-relaxed mb-6">
                    "{t.text}"
                  </p>
                </div>
                <div className="flex items-center space-x-4 pt-4 border-t border-slate-800">
                  <img src={t.image} alt={t.name} className="w-12 h-12 rounded-full object-cover border border-slate-700" />
                  <div>
                    <div className="text-white font-bold text-sm">{t.name}</div>
                    <div className="text-slate-400 text-sm ">{t.role}</div>
                    <div className="text-slate-500 text-[11px] ">{t.organization}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>



      {/* SECTION 14: FINAL BUSINESS PROPOSAL FOOTER - Deep Executive Navy */}
      <section className="py-24 bg-[#0F172A] text-white text-center">
        <div className="container mx-auto px-6 max-w-4xl">
          <h2 className="text-3xl font-extrabold text-white mb-4">
            Partner with HOASP Global for Healthcare Excellence
          </h2>
          <p className="text-slate-300 text-sm md:text-base leading-relaxed mb-8">
            Whether you are modernizing a 1,000+ bed teaching hospital, establishing a public health disease surveillance program, or launching an AI-powered mHealth startup, HOASP Global delivers secure, scalable, and compliant healthcare technology solutions. Contact our engineering executive team today to initiate your digital transformation.
          </p>
          <a href="#consultation">
            <Button className="bg-gradient-to-r from-cyan-400 to-blue-600 hover:from-cyan-500 hover:to-blue-700 text-black font-extrabold px-8 py-4 rounded-xl text-base shadow-xl hover:scale-105 transition-all">
              Initiate Healthcare Discussion →
            </Button>
          </a>
        </div>
      </section>

    </div>
  );
};

export default Healthcare;